import React, { useState, useEffect, useMemo } from 'react';
import UserLayout from '../../components/layouts/UserLayout';
import { memberInvestmentAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  Building2, 
  PieChart, 
  DollarSign, 
  Ticket,
  ChevronRight,
  Loader2,
  BarChart3,
  Percent,
  LineChart as LineChartIcon,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const MemberInvestments = () => {
  const [loading, setLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [totalShares, setTotalShares] = useState(0);
  
  // P&L view
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [plData, setPlData] = useState(null);
  const [loadingPL, setLoadingPL] = useState(false);
  
  // Historical data
  const [historyData, setHistoryData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState('monthly');

  // Computed history stats
  const historyStats = useMemo(() => {
    if (!historyData?.data?.length) return null;
    return {
      totalProfit: historyData.data.reduce((sum, d) => sum + d.your_profit_loss_eur, 0),
      totalRevenue: historyData.data.reduce((sum, d) => sum + d.revenue_eur, 0),
      totalTicketsSold: historyData.data.reduce((sum, d) => sum + d.tickets_sold, 0),
      profitableMonths: historyData.data.filter(d => d.profit_loss_eur > 0).length,
      totalMonths: historyData.data.length
    };
  }, [historyData]);

  // Generate month options (last 12 months + next 6 months)
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    
    // Past 12 months
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ key, label });
    }
    
    // Next 6 months
    for (let i = 1; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ key, label });
    }
    
    return options;
  }, []);

  useEffect(() => {
    loadInvestments();
  }, []);

  useEffect(() => {
    if (selectedInvestment && selectedMonth) {
      loadPLData();
    }
  }, [selectedInvestment, selectedMonth]);

  useEffect(() => {
    if (selectedInvestment) {
      loadHistoryData();
    }
  }, [selectedInvestment]);

  const loadInvestments = async () => {
    try {
      const res = await memberInvestmentAPI.getInvestments();
      setInvestments(res.data.investments || []);
      setTotalValue(res.data.total_investment_value_eur || 0);
      setTotalShares(res.data.total_shares || 0);
      
      // Auto-select first investment
      if (res.data.investments?.length > 0) {
        setSelectedInvestment(res.data.investments[0].monument_id);
        // Default to current month
        const now = new Date();
        setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
      }
    } catch (error) {
      console.error('Failed to load investments:', error);
      toast.error('Failed to load investments');
    } finally {
      setLoading(false);
    }
  };

  const loadHistoryData = async () => {
    if (!selectedInvestment) return;
    
    setLoadingHistory(true);
    try {
      const res = await memberInvestmentAPI.getInvestmentHistory(selectedInvestment, 12);
      setHistoryData(res.data);
    } catch (error) {
      console.error('Failed to load history data:', error);
      setHistoryData(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadPLData = async () => {
    if (!selectedInvestment || !selectedMonth) return;
    
    setLoadingPL(true);
    try {
      const res = await memberInvestmentAPI.getInvestmentPL(selectedInvestment, selectedMonth);
      setPlData(res.data);
    } catch (error) {
      console.error('Failed to load P&L data:', error);
      setPlData(null);
      if (error.response?.status === 404) {
        toast.error('No investment data found for this monument');
      }
    } finally {
      setLoadingPL(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <UserLayout title="My Investments" subtitle="View your investment portfolio">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </UserLayout>
    );
  }

  if (investments.length === 0) {
    return (
      <UserLayout title="My Investments" subtitle="View your investment portfolio">
        <Card className="text-center py-12">
          <CardContent>
            <PieChart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Investments Yet</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              You don&apos;t have any investment shares in monuments yet. 
              Contact your administrator to learn about investment opportunities.
            </p>
          </CardContent>
        </Card>
      </UserLayout>
    );
  }

  const selectedInvestmentData = investments.find(i => i.monument_id === selectedInvestment);

  return (
    <UserLayout title="My Investments" subtitle="View your investment portfolio and P&L">
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="investment-summary">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm font-medium">Total Investment Value</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(totalValue)}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm font-medium">Total Shares</p>
                  <p className="text-2xl font-bold mt-1">{totalShares}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <PieChart className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-violet-500 to-violet-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-violet-100 text-sm font-medium">Monuments Invested</p>
                  <p className="text-2xl font-bold mt-1">{investments.length}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Investment List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Your Investments
            </CardTitle>
            <CardDescription>Click on an investment to view detailed P&L</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="investment-list">
              {investments.map((investment) => (
                <div
                  key={investment.monument_id}
                  onClick={() => setSelectedInvestment(investment.monument_id)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedInvestment === investment.monument_id
                      ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500/20'
                      : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
                  }`}
                  data-testid={`investment-${investment.monument_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedInvestment === investment.monument_id
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{investment.monument_name}</h4>
                        <p className="text-sm text-slate-500">
                          {investment.shares} shares ({investment.ownership_percentage}% ownership)
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">{formatCurrency(investment.investment_value_eur)}</p>
                      <p className="text-xs text-slate-500">
                        @ {formatCurrency(investment.share_price_eur)}/share
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 ml-2 transition-transform ${
                      selectedInvestment === investment.monument_id ? 'text-indigo-600' : 'text-slate-400'
                    }`} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* P&L Report with Tabs */}
        {selectedInvestment && (
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                    Investment Performance
                  </CardTitle>
                  <CardDescription>
                    {selectedInvestmentData?.monument_name || 'Select an investment'}
                  </CardDescription>
                </div>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
                  <TabsList className="grid w-full grid-cols-2 sm:w-[280px]">
                    <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="tab-monthly">
                      <Calendar className="w-4 h-4" />
                      Monthly
                    </TabsTrigger>
                    <TabsTrigger value="history" className="flex items-center gap-2" data-testid="tab-history">
                      <LineChartIcon className="w-4 h-4" />
                      12-Month Trend
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              {activeTab === 'monthly' && (
                <>
                  <div className="flex justify-end mb-4">
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="w-[200px]" data-testid="month-selector">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {monthOptions.map((option) => (
                          <SelectItem key={option.key} value={option.key}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {loadingPL ? (
                    <div className="flex items-center justify-center h-48">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                    </div>
                  ) : plData ? (
                    <div className="space-y-6" data-testid="pl-report">
                      {/* P&L Summary Cards */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                          <div className="flex items-center gap-2 text-blue-600 mb-1">
                            <DollarSign className="w-4 h-4" />
                            <span className="text-xs font-medium">Revenue</span>
                          </div>
                          <p className="text-xl font-bold text-blue-700">{formatCurrency(plData.total_revenue_eur)}</p>
                        </div>
                        
                        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                          <div className="flex items-center gap-2 text-amber-600 mb-1">
                            <DollarSign className="w-4 h-4" />
                            <span className="text-xs font-medium">Cost</span>
                          </div>
                          <p className="text-xl font-bold text-amber-700">{formatCurrency(plData.total_cost_eur)}</p>
                        </div>
                        
                        <div className={`p-4 rounded-lg border ${
                          plData.profit_loss_eur >= 0 
                            ? 'bg-emerald-50 border-emerald-200' 
                            : 'bg-red-50 border-red-200'
                        }`}>
                          <div className={`flex items-center gap-2 mb-1 ${
                            plData.profit_loss_eur >= 0 ? 'text-emerald-600' : 'text-red-600'
                          }`}>
                            {plData.profit_loss_eur >= 0 ? (
                              <TrendingUp className="w-4 h-4" />
                            ) : (
                              <TrendingDown className="w-4 h-4" />
                            )}
                            <span className="text-xs font-medium">Net {plData.profit_loss_eur >= 0 ? 'Profit' : 'Loss'}</span>
                          </div>
                          <p className={`text-xl font-bold ${
                            plData.profit_loss_eur >= 0 ? 'text-emerald-700' : 'text-red-700'
                          }`}>
                            {formatCurrency(Math.abs(plData.profit_loss_eur))}
                          </p>
                        </div>
                        
                        <div className={`p-4 rounded-lg border ${
                          plData.your_profit_loss_eur >= 0 
                            ? 'bg-indigo-50 border-indigo-200' 
                            : 'bg-red-50 border-red-200'
                        }`}>
                          <div className={`flex items-center gap-2 mb-1 ${
                            plData.your_profit_loss_eur >= 0 ? 'text-indigo-600' : 'text-red-600'
                          }`}>
                            <PieChart className="w-4 h-4" />
                            <span className="text-xs font-medium">Your {plData.your_profit_loss_eur >= 0 ? 'Profit' : 'Loss'}</span>
                          </div>
                          <p className={`text-xl font-bold ${
                            plData.your_profit_loss_eur >= 0 ? 'text-indigo-700' : 'text-red-700'
                          }`}>
                            {formatCurrency(Math.abs(plData.your_profit_loss_eur))}
                          </p>
                        </div>
                      </div>

                      {/* Details Table */}
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b bg-slate-50">
                              <td className="px-4 py-3 font-medium text-slate-600">Period</td>
                              <td className="px-4 py-3 text-right text-slate-900">{plData.month_display}</td>
                            </tr>
                            <tr className="border-b">
                              <td className="px-4 py-3 font-medium text-slate-600 flex items-center gap-2">
                                <Ticket className="w-4 h-4" />
                                Tickets Available
                              </td>
                              <td className="px-4 py-3 text-right text-slate-900">{plData.tickets_available}</td>
                            </tr>
                            <tr className="border-b bg-slate-50">
                              <td className="px-4 py-3 font-medium text-slate-600 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Tickets Sold
                              </td>
                              <td className="px-4 py-3 text-right text-slate-900">
                                {plData.tickets_sold}
                                <span className="text-slate-500 ml-2">({plData.sell_rate}%)</span>
                              </td>
                            </tr>
                            <tr className="border-b">
                              <td className="px-4 py-3 font-medium text-slate-600 flex items-center gap-2">
                                <PieChart className="w-4 h-4" />
                                Your Shares
                              </td>
                              <td className="px-4 py-3 text-right text-slate-900">
                                {plData.shares} of {plData.total_shares} total
                              </td>
                            </tr>
                            <tr className="border-b bg-slate-50">
                              <td className="px-4 py-3 font-medium text-slate-600 flex items-center gap-2">
                                <Percent className="w-4 h-4" />
                                Profit Per Share
                              </td>
                              <td className={`px-4 py-3 text-right font-medium ${
                                plData.profit_per_share_eur >= 0 ? 'text-emerald-600' : 'text-red-600'
                              }`}>
                                {formatCurrency(plData.profit_per_share_eur)}
                              </td>
                            </tr>
                            <tr className="bg-indigo-50">
                              <td className="px-4 py-3 font-semibold text-indigo-700">
                                Your {plData.your_profit_loss_eur >= 0 ? 'Profit' : 'Loss'} ({plData.shares} shares)
                              </td>
                              <td className={`px-4 py-3 text-right text-lg font-bold ${
                                plData.your_profit_loss_eur >= 0 ? 'text-emerald-600' : 'text-red-600'
                              }`}>
                                {formatCurrency(Math.abs(plData.your_profit_loss_eur))}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {plData.tickets_available === 0 && (
                        <div className="text-center py-4 text-slate-500 bg-slate-50 rounded-lg">
                          <p>No ticket data available for this month yet.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Select a month to view the P&L report</p>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'history' && (
                <>
                  {loadingHistory ? (
                    <div className="flex items-center justify-center h-[400px]">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                    </div>
                  ) : historyData && historyData.data?.length > 0 ? (
                    <div className="space-y-6" data-testid="history-chart">
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className={`p-4 rounded-lg border ${
                          historyStats.totalProfit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                        }`}>
                          <p className="text-xs font-medium text-slate-500 mb-1">12-Month Total {historyStats.totalProfit >= 0 ? 'Profit' : 'Loss'}</p>
                          <p className={`text-2xl font-bold ${historyStats.totalProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(Math.abs(historyStats.totalProfit))}
                          </p>
                        </div>
                        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                          <p className="text-xs font-medium text-slate-500 mb-1">Total Revenue</p>
                          <p className="text-2xl font-bold text-blue-600">{formatCurrency(historyStats.totalRevenue)}</p>
                        </div>
                        <div className="p-4 rounded-lg bg-violet-50 border border-violet-200">
                          <p className="text-xs font-medium text-slate-500 mb-1">Tickets Sold</p>
                          <p className="text-2xl font-bold text-violet-600">{historyStats.totalTicketsSold}</p>
                        </div>
                        <div className="p-4 rounded-lg bg-indigo-50 border border-indigo-200">
                          <p className="text-xs font-medium text-slate-500 mb-1">Profitable Months</p>
                          <p className="text-2xl font-bold text-indigo-600">{historyStats.profitableMonths} / {historyStats.totalMonths}</p>
                        </div>
                      </div>

                      {/* Profit/Loss Chart */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-indigo-600" />
                          Your Profit/Loss Trend
                        </h4>
                        <ResponsiveContainer width="100%" height={280}>
                          <AreaChart data={historyData.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="lossGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis 
                              dataKey="month_label" 
                              tick={{ fontSize: 11, fill: '#64748b' }}
                              tickLine={false}
                              axisLine={{ stroke: '#e2e8f0' }}
                            />
                            <YAxis 
                              tick={{ fontSize: 11, fill: '#64748b' }}
                              tickLine={false}
                              axisLine={{ stroke: '#e2e8f0' }}
                              tickFormatter={(value) => `€${value}`}
                            />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: 'white', 
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                              }}
                              formatter={(value, name) => [formatCurrency(value), name === 'your_profit_loss_eur' ? 'Your P&L' : name]}
                              labelStyle={{ fontWeight: 600 }}
                            />
                            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                            <Area
                              type="monotone"
                              dataKey="your_profit_loss_eur"
                              stroke="#6366f1"
                              strokeWidth={2}
                              fill="url(#profitGradient)"
                              name="Your P&L"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Revenue vs Cost Chart */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-blue-600" />
                          Revenue vs Cost
                        </h4>
                        <ResponsiveContainer width="100%" height={280}>
                          <LineChart data={historyData.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis 
                              dataKey="month_label" 
                              tick={{ fontSize: 11, fill: '#64748b' }}
                              tickLine={false}
                              axisLine={{ stroke: '#e2e8f0' }}
                            />
                            <YAxis 
                              tick={{ fontSize: 11, fill: '#64748b' }}
                              tickLine={false}
                              axisLine={{ stroke: '#e2e8f0' }}
                              tickFormatter={(value) => `€${value}`}
                            />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: 'white', 
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                              }}
                              formatter={(value) => formatCurrency(value)}
                              labelStyle={{ fontWeight: 600 }}
                            />
                            <Legend 
                              wrapperStyle={{ paddingTop: '20px' }}
                              formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="revenue_eur" 
                              stroke="#3b82f6" 
                              strokeWidth={2}
                              dot={{ fill: '#3b82f6', strokeWidth: 0, r: 4 }}
                              activeDot={{ r: 6 }}
                              name="Revenue"
                            />
                            <Line 
                              type="monotone" 
                              dataKey="cost_eur" 
                              stroke="#f59e0b" 
                              strokeWidth={2}
                              dot={{ fill: '#f59e0b', strokeWidth: 0, r: 4 }}
                              activeDot={{ r: 6 }}
                              name="Cost"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Monthly Data Table */}
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-slate-100 px-4 py-3 border-b">
                          <h4 className="text-sm font-semibold text-slate-700">Monthly Breakdown</h4>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                <th className="px-4 py-3 text-left font-medium">Month</th>
                                <th className="px-4 py-3 text-right font-medium">Revenue</th>
                                <th className="px-4 py-3 text-right font-medium">Cost</th>
                                <th className="px-4 py-3 text-right font-medium">Net P&L</th>
                                <th className="px-4 py-3 text-right font-medium">Your P&L</th>
                                <th className="px-4 py-3 text-right font-medium">Tickets</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {historyData.data.map((month, idx) => (
                                <tr key={month.month_key} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                  <td className="px-4 py-3 font-medium text-slate-900">{month.month_label}</td>
                                  <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(month.revenue_eur)}</td>
                                  <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(month.cost_eur)}</td>
                                  <td className={`px-4 py-3 text-right font-medium ${
                                    month.profit_loss_eur >= 0 ? 'text-emerald-600' : 'text-red-600'
                                  }`}>
                                    {formatCurrency(month.profit_loss_eur)}
                                  </td>
                                  <td className={`px-4 py-3 text-right font-semibold ${
                                    month.your_profit_loss_eur >= 0 ? 'text-emerald-600' : 'text-red-600'
                                  }`}>
                                    {formatCurrency(month.your_profit_loss_eur)}
                                  </td>
                                  <td className="px-4 py-3 text-right text-slate-600">
                                    {month.tickets_sold}/{month.tickets_available}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <LineChartIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No historical data available yet.</p>
                      <p className="text-sm mt-1">Data will appear as ticket activity is recorded.</p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </UserLayout>
  );
};

export default MemberInvestments;
